import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Alert,
  Stack,
  IconButton,
  Tooltip,
  useMediaQuery,
  useTheme,
  Grid,
  Switch,
  FormControlLabel
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  Cloud as CloudIcon,
  CloudOff as CloudOffIcon,
  Storage as StorageIcon,
  Dns as DnsIcon,
  Folder as FolderIcon,
  Link as LinkIcon,
  AutoFixHigh as AutoFixIcon
} from '@mui/icons-material';
import axios from 'axios';
import { 
  SectionTitle, 
  GlassContainer, 
  PrimaryButton, 
  DangerButton,
  StatusChipBase,
  getStatusChipStyles,
  BodyText,
  SecondaryText,
  CompactCard
} from '../styles/sharedStyles';

const NFSMounts = ({ apiBaseUrl, token }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [mounts, setMounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [actionLoading, setActionLoading] = useState(null);
  const [automountEnabled, setAutomountEnabled] = useState(false);
  const [automountLoading, setAutomountLoading] = useState(false);

  const fetchAutomountStatus = useCallback(async () => {
    try {
      const response = await axios.get(`${apiBaseUrl}/api/nfs/automount/status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAutomountEnabled(response.data.enabled);
    } catch (err) {
      console.error("Failed to fetch automount status:", err);
    }
  }, [apiBaseUrl, token]);

  const fetchMountsStatus = useCallback(async (isSilent = false) => {
    try {
      if (!isSilent) setLoading(true);
      if (!isSilent) setError(null);
      const response = await axios.get(`${apiBaseUrl}/api/nfs/mounts`, {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      });
      setMounts(response.data.mounts || []);
    } catch (err) {
      if (!isSilent) {
        setError(err.response?.status === 401 ? 'Session expired' : err.message || 'Failed to load NFS mounts');
        setMounts([]);
      }
    } finally {
      if (!isSilent) setLoading(false);
    }
  }, [apiBaseUrl, token]);

  useEffect(() => {
    fetchMountsStatus();
    fetchAutomountStatus();
    // Auto-refresh every 30 seconds
    const interval = setInterval(() => fetchMountsStatus(true), 30000);
    return () => clearInterval(interval);
  }, [fetchMountsStatus, fetchAutomountStatus]);

  const handleAutomountToggle = async (event) => {
    const newEnabled = event.target.checked;
    setAutomountLoading(true);
    try {
      await axios.post(`${apiBaseUrl}/api/nfs/automount/toggle`, 
        { enabled: newEnabled },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setAutomountEnabled(newEnabled);
    } catch (err) {
      setError("Failed to update automount setting");
      console.error(err);
    } finally {
      setAutomountLoading(false);
    }
  };

  const handleMount = async (mountPoint) => {
    setActionLoading(mountPoint);
    setError(null);
    try {
      await axios.post(`${apiBaseUrl}/api/nfs/mount`, 
        { mount_point: mountPoint },
        {
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
        }
      );
      // Refresh status
      await fetchMountsStatus();
    } catch (err) {
      setError(err.response?.data?.detail || err.message || `Failed to mount ${mountPoint}`);
    } finally {
      setActionLoading(null);
    }
  };

  const handleUnmount = async (mountPoint) => {
    setActionLoading(mountPoint);
    setError(null);
    try {
      await axios.post(`${apiBaseUrl}/api/nfs/unmount`, 
        { mount_point: mountPoint },
        {
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
        }
      );
      // Refresh status
      await fetchMountsStatus();
    } catch (err) {
      setError(err.response?.data?.detail || err.message || `Failed to unmount ${mountPoint}`);
    } finally {
      setActionLoading(null);
    }
  };

  const handleMountAll = async () => {
    setActionLoading('all');
    setError(null);
    try {
      await axios.post(`${apiBaseUrl}/api/nfs/mount-all`, {}, {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      });
      await fetchMountsStatus();
    } catch (err) {
      setError(err.response?.data?.detail || err.message || 'Failed to mount all NFS shares');
    } finally {
      setActionLoading(null);
    }
  };

  const handleUnmountAll = async () => {
    setActionLoading('all');
    setError(null);
    try {
      await axios.post(`${apiBaseUrl}/api/nfs/unmount-all`, {}, {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      });
      await fetchMountsStatus();
    } catch (err) {
      setError(err.response?.data?.detail || err.message || 'Failed to unmount all NFS shares');
    } finally {
      setActionLoading(null);
    }
  };

  const renderMobileView = () => (
    <Grid container spacing={2}>
      {mounts.map((mount) => (
        <Grid item xs={12} key={mount.mount_point}>
          <CompactCard sx={{ 
            p: 2.5, 
            background: 'rgba(255, 255, 255, 0.03)',
            border: '1px solid rgba(255, 255, 255, 0.08)',
            position: 'relative'
          }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
              <Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                  <DnsIcon sx={{ fontSize: 16, color: '#00d4ff' }} />
                  <Typography sx={{ color: '#fff', fontWeight: 600, fontSize: '0.95rem' }}>
                    {mount.server}
                  </Typography>
                </Box>
                <StatusChipBase
                  icon={mount.is_mounted ? <CloudIcon style={{ fontSize: 14 }} /> : <CloudOffIcon style={{ fontSize: 14 }} />}
                  label={mount.is_mounted ? 'Mounted' : 'Unmounted'}
                  sx={{ ...getStatusChipStyles(mount.is_mounted ? 'active' : 'inactive'), height: '22px' }}
                />
              </Box>
              <IconButton 
                size="small" 
                onClick={() => fetchMountsStatus()}
                sx={{ color: 'rgba(255,255,255,0.3)' }}
              >
                <RefreshIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </Box>

            <Stack spacing={1.5} sx={{ mb: 3 }}>
              <Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                  <FolderIcon sx={{ fontSize: 14, color: 'rgba(255,255,255,0.4)' }} />
                  <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.4)', textTransform: 'uppercase' }}>Export</Typography>
                </Box>
                <Typography sx={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.85rem', wordBreak: 'break-all', pl: 3 }}>
                  {mount.export}
                </Typography>
              </Box>
              
              <Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                  <LinkIcon sx={{ fontSize: 14, color: 'rgba(255,255,255,0.4)' }} />
                  <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.4)', textTransform: 'uppercase' }}>Mount Point</Typography>
                </Box>
                <Typography sx={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.85rem', wordBreak: 'break-all', pl: 3 }}>
                  {mount.mount_point}
                </Typography>
              </Box>
            </Stack>

            <Grid container spacing={1.5}>
              <Grid item xs={6}>
                <PrimaryButton
                  fullWidth
                  size="small"
                  onClick={() => handleMount(mount.mount_point)}
                  disabled={mount.is_mounted || actionLoading === mount.mount_point}
                  sx={{ py: 1, fontSize: '0.85rem' }}
                >
                  {actionLoading === mount.mount_point ? (
                    <CircularProgress size={16} color="inherit" />
                  ) : (
                    'Mount'
                  )}
                </PrimaryButton>
              </Grid>
              <Grid item xs={6}>
                <DangerButton
                  fullWidth
                  size="small"
                  onClick={() => handleUnmount(mount.mount_point)}
                  disabled={!mount.is_mounted || actionLoading === mount.mount_point}
                  sx={{ py: 1, fontSize: '0.85rem' }}
                >
                  {actionLoading === mount.mount_point ? (
                    <CircularProgress size={16} color="inherit" />
                  ) : (
                    'Unmount'
                  )}
                </DangerButton>
              </Grid>
            </Grid>
          </CompactCard>
        </Grid>
      ))}
    </Grid>
  );

  return (
    <Box>
      <SectionTitle sx={{ mb: 3 }}>
        <StorageIcon sx={{ color: '#00d4ff' }} /> NFS Mounts Management
      </SectionTitle>

      <GlassContainer sx={{ 
        p: { xs: 2, sm: 3, md: 4 },
        borderRadius: '24px',
        mb: 4
      }}>
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'flex-start', 
          mb: 4,
          flexDirection: { xs: 'column', sm: 'row' },
          gap: 3
        }}>
          <Box sx={{ flex: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
              <BodyText sx={{ color: '#fff', fontWeight: 500, m: 0 }}>
                TrueNAS NFS Integration
              </BodyText>
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                background: 'rgba(0, 212, 255, 0.05)', 
                px: 1.5, 
                py: 0.5, 
                borderRadius: '20px',
                border: '1px solid rgba(0, 212, 255, 0.1)'
              }}>
                <FormControlLabel
                  control={
                    <Switch
                      size="small"
                      checked={automountEnabled}
                      onChange={handleAutomountToggle}
                      disabled={automountLoading}
                      sx={{
                        '& .MuiSwitch-switchBase.Mui-checked': { color: '#00d4ff' },
                        '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#00d4ff' }
                      }}
                    />
                  }
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <AutoFixIcon sx={{ fontSize: 16, color: automountEnabled ? '#00d4ff' : 'rgba(255,255,255,0.3)' }} />
                      <Typography variant="caption" sx={{ color: automountEnabled ? '#00d4ff' : 'rgba(255,255,255,0.5)', fontWeight: 600 }}>
                        AUTO-MOUNT {automountEnabled ? 'ON' : 'OFF'}
                      </Typography>
                    </Box>
                  }
                  sx={{ m: 0 }}
                />
              </Box>
            </Box>
            <SecondaryText>
              Manage NFS mounts from TrueNAS. Mounts are automatically configured to persist across reboots.
              NFS mounts are configured for read-only access for security reasons.
            </SecondaryText>
          </Box>
          
          <Stack 
            direction={isMobile ? "column" : "row"} 
            spacing={2} 
            sx={{ width: { xs: '100%', sm: 'auto' }, alignSelf: { xs: 'stretch', sm: 'center' } }}
          >
            <Stack direction="row" spacing={2} sx={{ flex: 1 }}>
              <PrimaryButton
                fullWidth={isMobile}
                onClick={handleMountAll}
                disabled={actionLoading === 'all' || loading}
                startIcon={actionLoading === 'all' ? <CircularProgress size={20} color="inherit" /> : <CloudIcon />}
                sx={{ minWidth: isMobile ? 'auto' : '130px', flex: 1 }}
              >
                Mount All
              </PrimaryButton>
              
              <DangerButton
                fullWidth={isMobile}
                onClick={handleUnmountAll}
                disabled={actionLoading === 'all' || loading}
                startIcon={actionLoading === 'all' ? <CircularProgress size={20} color="inherit" /> : <CloudOffIcon />}
                sx={{ minWidth: isMobile ? 'auto' : '130px', flex: 1 }}
              >
                Unmount All
              </DangerButton>
            </Stack>

            <Tooltip title="Refresh mount status">
              <IconButton
                onClick={() => fetchMountsStatus()}
                disabled={loading || actionLoading !== null}
                sx={{ 
                  color: '#00d4ff',
                  background: 'rgba(0, 212, 255, 0.1)',
                  border: '1px solid rgba(0, 212, 255, 0.3)',
                  borderRadius: '12px',
                  alignSelf: isMobile ? 'center' : 'auto',
                  '&:hover': {
                    background: 'rgba(0, 212, 255, 0.2)',
                  }
                }}
              >
                <RefreshIcon sx={{ animation: loading ? 'spin 1s linear infinite' : 'none' }} />
              </IconButton>
            </Tooltip>
          </Stack>
        </Box>

        {error && (
          <Alert 
            severity="error" 
            sx={{ 
              mb: 3, 
              borderRadius: '12px',
              background: 'rgba(255, 68, 68, 0.1)',
              border: '1px solid rgba(255, 68, 68, 0.2)',
              color: '#ff8888',
              '& .MuiAlert-icon': { color: '#ff4444' }
            }}
          >
            {error}
          </Alert>
        )}

        {loading && mounts.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 8 }}>
            <CircularProgress sx={{ color: '#00d4ff' }} />
            <Typography sx={{ mt: 2, color: 'rgba(255,255,255,0.5)' }}>Loading mount points...</Typography>
          </Box>
        ) : mounts.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 8, color: 'rgba(255,255,255,0.5)' }}>
            <StorageIcon sx={{ fontSize: 48, opacity: 0.2, mb: 1 }} />
            <Typography>No NFS mounts configured</Typography>
          </Box>
        ) : isMobile ? (
          renderMobileView()
        ) : (
          <TableContainer component={Paper} sx={{ 
            background: 'rgba(0, 0, 0, 0.2)',
            borderRadius: '16px',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            overflow: 'hidden',
            boxShadow: 'none'
          }}>
            <Table sx={{ minWidth: 700 }} aria-label="nfs mounts table">
              <TableHead>
                <TableRow sx={{ background: 'rgba(255, 255, 255, 0.03)' }}>
                  <TableCell sx={{ color: '#00d4ff', fontWeight: 600, borderBottom: '1px solid rgba(255, 255, 255, 0.1)' }}>Server</TableCell>
                  <TableCell sx={{ color: '#00d4ff', fontWeight: 600, borderBottom: '1px solid rgba(255, 255, 255, 0.1)' }}>Export Path</TableCell>
                  <TableCell sx={{ color: '#00d4ff', fontWeight: 600, borderBottom: '1px solid rgba(255, 255, 255, 0.1)' }}>Mount Point</TableCell>
                  <TableCell sx={{ color: '#00d4ff', fontWeight: 600, borderBottom: '1px solid rgba(255, 255, 255, 0.1)', width: '120px' }}>Status</TableCell>
                  <TableCell sx={{ color: '#00d4ff', fontWeight: 600, borderBottom: '1px solid rgba(255, 255, 255, 0.1)', width: '220px', textAlign: 'right' }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {mounts.map((mount) => (
                  <TableRow 
                    key={mount.mount_point}
                    sx={{
                      '&:hover': {
                        background: 'rgba(255, 255, 255, 0.02)'
                      },
                      '& td': { borderBottom: '1px solid rgba(255, 255, 255, 0.05)' }
                    }}
                  >
                    <TableCell sx={{ color: 'rgba(255,255,255,0.9)', fontWeight: 500 }}>{mount.server}</TableCell>
                    <TableCell sx={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.875rem', maxWidth: '300px', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      <Tooltip title={mount.export}>
                        <span>{mount.export}</span>
                      </Tooltip>
                    </TableCell>
                    <TableCell sx={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.875rem' }}>{mount.mount_point}</TableCell>
                    <TableCell>
                      <StatusChipBase
                        icon={mount.is_mounted ? <CloudIcon style={{ fontSize: 16 }} /> : <CloudOffIcon style={{ fontSize: 16 }} />}
                        label={mount.is_mounted ? 'Mounted' : 'Unmounted'}
                        sx={getStatusChipStyles(mount.is_mounted ? 'active' : 'inactive')}
                      />
                    </TableCell>
                    <TableCell sx={{ textAlign: 'right' }}>
                      <Stack direction="row" spacing={1} justifyContent="flex-end">
                        <PrimaryButton
                          size="small"
                          onClick={() => handleMount(mount.mount_point)}
                          disabled={mount.is_mounted || actionLoading === mount.mount_point}
                          sx={{ 
                            px: 2, 
                            py: 0.5, 
                            fontSize: '0.8125rem',
                            minWidth: '85px'
                          }}
                        >
                          {actionLoading === mount.mount_point ? (
                            <CircularProgress size={16} color="inherit" />
                          ) : (
                            'Mount'
                          )}
                        </PrimaryButton>
                        
                        <DangerButton
                          size="small"
                          onClick={() => handleUnmount(mount.mount_point)}
                          disabled={!mount.is_mounted || actionLoading === mount.mount_point}
                          sx={{ 
                            px: 2, 
                            py: 0.5, 
                            fontSize: '0.8125rem',
                            minWidth: '85px'
                          }}
                        >
                          {actionLoading === mount.mount_point ? (
                            <CircularProgress size={16} color="inherit" />
                          ) : (
                            'Unmount'
                          )}
                        </DangerButton>
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        <Box sx={{ mt: 4, pt: 2, borderTop: '1px solid rgba(255, 255, 255, 0.05)' }}>
          <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.4)', display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box component="span" sx={{ width: 6, height: 6, borderRadius: '50%', background: '#00d4ff' }} />
            Note: Mount operations require the container to have SYS_ADMIN capabilities. 
          </Typography>
        </Box>
      </GlassContainer>
    </Box>
  );
};

export default NFSMounts;
